// useRecPlayback — feature-local hooks tying the recPlayback lib together
// (frontend plan §6). No global store: per-feature hooks + the api/ws singletons.
//
// useReconciledTimeline(deviceIds, range):
//   per-device, fetches server events (REST) + cloud clips (REST) + sdcard
//   range-events (op:get tri-state, gated by pb.sdcard), runs reconcile (§3.6) with a
//   time-aware PENDING grace, and re-evaluates when each grace window expires. Holds a
//   per-device firstSeen map (eid → first-seen µs) as the grace anchor for zero/absent
//   event_ts (R14). Rows are merged across devices, newest-first.
//
// useDayList / useDayRanges: View-2 axis data (c1/c2), each tri-state aware.

import { useCallback, useEffect, useRef, useState } from 'react';
import { compareRowsNewestFirst } from '../../features/events/eventsLib';
import { GRACE_US, makeCloudExists, reconcile, type ReconciledRow } from '../../lib/recPlayback/reconcile';
import { queryDayList, queryDayRanges, queryRangeEvents, type PbDay, type PbRange, type SdcardState } from '../../lib/recPlayback/sdcardQuery';
import { fetchCloudClips, fetchServerEvents } from '../../lib/recPlayback/recRest';
import { recFlag } from '../../lib/recPlayback/flags';
import { signaling } from '../../lib/ws';

/**
 * True once the signaling WebSocket is `open` (post-signin). The op:get queries below
 * ride this socket, so firing them before it connects (e.g. right after a hard reload)
 * rejects with "not connected" → a stuck UNREACHABLE. Gating on this + re-running when
 * it flips open fixes that race. Reads the CURRENT state first (mid-session navigation
 * has it already open, so the 'state' event would never re-fire).
 */
function useSignalingReady(): boolean {
  const [ready, setReady] = useState(() => signaling.getState() === 'open');
  useEffect(() => {
    setReady(signaling.getState() === 'open');
    return signaling.on('state', (s) => setReady(s === 'open'));
  }, []);
  return ready;
}

export interface RecRange {
  fromUs: number;
  toUs: number;
}

export interface DeviceRef {
  id: string;
  name: string;
  /** Recording destination (from DeviceSummary.recording.location). 'cloud' → the
   * event list + playback come from the cloud recordings API (S3), NOT the device's
   * sdcard markers / device-VOD. Absent/'sdcard' → the sdcard path. */
  location?: 'cloud' | 'sdcard';
}

export interface ReconciledTimelineState {
  rows: Array<{ deviceId: string; row: ReconciledRow }>;
  loading: boolean;
  /** per-device sdcard tri-state (UNREACHABLE → UI shows "sdcard unknown"). */
  sdcardStates: Record<string, SdcardState>;
  error: string | null;
  reload: () => void;
}

const nowUs = () => Date.now() * 1000;

/**
 * Reconciled, cross-device event timeline for [range]. Re-runs on device/range
 * change and on reload(); schedules a re-evaluation when the soonest PENDING grace
 * window elapses so PENDING rows resolve to CLOUD/EVENT_NO_RECORDING in place.
 */
export function useReconciledTimeline(devices: DeviceRef[], range: RecRange): ReconciledTimelineState {
  const [rows, setRows] = useState<Array<{ deviceId: string; row: ReconciledRow }>>([]);
  const [sdcardStates, setSdcardStates] = useState<Record<string, SdcardState>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [graceTick, setGraceTick] = useState(0);

  // per-device firstSeen map persists across re-renders (grace anchor, R14).
  const firstSeenRef = useRef<Map<string, Map<string, number>>>(new Map());
  const graceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const aliveRef = useRef(true);

  const deviceKey = devices.map((d) => d.id).join(',');
  const sdcardEnabled = recFlag('pb.sdcard');

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      if (graceTimerRef.current) clearTimeout(graceTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    setError(null);

    (async () => {
      const merged: Array<{ deviceId: string; row: ReconciledRow }> = [];
      const states: Record<string, SdcardState> = {};
      try {
        for (const dev of devices) {
          // server events + cloud clips in parallel; sdcard only when enabled.
          const [serverEvents, cloudClips, sdcard] = await Promise.all([
            fetchServerEvents(dev.id, range.fromUs, range.toUs, 'cloud', ac.signal),
            fetchCloudClips(dev.id, range.fromUs, range.toUs, ac.signal),
            sdcardEnabled
              ? queryRangeEvents(dev.id, range.fromUs, range.toUs)
              : Promise.resolve({ state: 'UNREACHABLE' as SdcardState, items: [] }),
          ]);
          if (ac.signal.aborted) return;

          // record/refresh firstSeen for grace anchoring (R14).
          let fs = firstSeenRef.current.get(dev.id);
          if (!fs) {
            fs = new Map();
            firstSeenRef.current.set(dev.id, fs);
          }
          const seenNow = nowUs();
          for (const se of serverEvents) if (!fs.has(se.eid)) fs.set(se.eid, seenNow);

          states[dev.id] = sdcardEnabled ? sdcard.state : 'EMPTY';
          const reconciled = reconcile({
            serverEvents,
            sdcardState: sdcardEnabled ? sdcard.state : 'EMPTY',
            sdcardEvents: sdcardEnabled ? sdcard.items : [],
            cloudExists: makeCloudExists(cloudClips),
            nowUs: nowUs(),
            firstSeen: fs,
          });
          for (const row of reconciled) merged.push({ deviceId: dev.id, row });
        }
        if (ac.signal.aborted || !aliveRef.current) return;
        merged.sort((a, b) =>
          compareRowsNewestFirst(
            { deviceId: a.deviceId, ev: { event_id: '', event_type: '', event_ts: a.row.event_ts_us, correlation_id: a.row.eid } },
            { deviceId: b.deviceId, ev: { event_id: '', event_type: '', event_ts: b.row.event_ts_us, correlation_id: b.row.eid } }
          )
        );
        setRows(merged);
        setSdcardStates(states);

        // Schedule a grace re-eval at the soonest PENDING expiry so labels self-resolve.
        if (graceTimerRef.current) clearTimeout(graceTimerRef.current);
        const pending = merged.filter((m) => m.row.label === 'PENDING');
        if (pending.length > 0) {
          const soonestExpiry = Math.min(
            ...pending.map((m) => {
              const fs = firstSeenRef.current.get(m.deviceId);
              const anchor = m.row.event_ts_us > 0 ? m.row.event_ts_us : fs?.get(m.row.eid) ?? nowUs();
              return anchor + GRACE_US;
            })
          );
          const delayMs = Math.max(1_000, Math.ceil((soonestExpiry - nowUs()) / 1000));
          graceTimerRef.current = setTimeout(() => {
            if (aliveRef.current) setGraceTick((n) => n + 1);
          }, delayMs);
        }
      } catch (e) {
        if (ac.signal.aborted || !aliveRef.current) return;
        setError(e instanceof Error ? e.message : 'failed to load timeline');
      } finally {
        if (!ac.signal.aborted && aliveRef.current) setLoading(false);
      }
    })();

    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceKey, range.fromUs, range.toUs, reloadKey, graceTick, sdcardEnabled]);

  const reload = useCallback(() => setReloadKey((n) => n + 1), []);
  return { rows, loading, sdcardStates, error, reload };
}

// ---- View-2 axis data ----

export interface DayListState {
  days: PbDay[];
  state: SdcardState;
  loading: boolean;
  reload: () => void;
}

/** c1 — which UTC+0 days have sdcard clips (cloud-day union is done by the view). */
export function useDayList(deviceId: string): DayListState {
  const [days, setDays] = useState<PbDay[]>([]);
  const [state, setState] = useState<SdcardState>('EMPTY');
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const ready = useSignalingReady();

  useEffect(() => {
    if (!ready) return; // wait for the ws; re-runs when ready flips true
    let alive = true;
    setLoading(true);
    queryDayList(deviceId).then(
      (res) => {
        if (!alive) return;
        setDays(res.items);
        setState(res.state);
        setLoading(false);
      },
      () => {
        if (!alive) return;
        setState('UNREACHABLE');
        setLoading(false);
      }
    );
    return () => {
      alive = false;
    };
  }, [deviceId, reloadKey, ready]);

  return { days, state, loading, reload: () => setReloadKey((n) => n + 1) };
}

export interface DayRangesState {
  ranges: PbRange[];
  state: SdcardState;
  loading: boolean;
  reload: () => void;
}

/** c2 — coalesced clip ranges for a UTC+0 day window. */
export function useDayRanges(deviceId: string, dayStartUs: number, dayEndUs: number, continuousOnly = false): DayRangesState {
  const [ranges, setRanges] = useState<PbRange[]>([]);
  const [state, setState] = useState<SdcardState>('EMPTY');
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const ready = useSignalingReady();

  useEffect(() => {
    if (!ready) return; // wait for the ws; this effect re-runs when ready flips true
    let alive = true;
    setLoading(true);
    queryDayRanges(deviceId, dayStartUs, dayEndUs, continuousOnly).then(
      (res) => {
        if (!alive) return;
        setRanges(res.items);
        setState(res.state);
        setLoading(false);
      },
      () => {
        if (!alive) return;
        setState('UNREACHABLE');
        setLoading(false);
      }
    );
    return () => {
      alive = false;
    };
  }, [deviceId, dayStartUs, dayEndUs, reloadKey, ready, continuousOnly]);

  return { ranges, state, loading, reload: () => setReloadKey((n) => n + 1) };
}

// ---- live (incremental) event tail ----

const EVENT_POLL_MS = 8000;

export interface LiveEventRow {
  eid: string;
  tsUs: number;
  eventType?: string;
  eventIdHex?: string;
  /** D16 record stamp — lets the continuous timeline filter/label live-tail events by mode. */
  recordMode?: string;
  recordLocation?: string;
}

/**
 * Incremental "tail" of NEW server events past `floorTsUs` — lets the timeline keep growing
 * as events fire WITHOUT re-reading the whole day. While `enabled` (the latest-record day),
 * it polls only `[max(lastSeen, floor)+1, toUs]` every few seconds, accumulating + de-duping
 * by eid. Resets on device/window change. The caller seeds `floorTsUs` with the newest event
 * it already knows (from the base full-day read), so this fetches only what arrived after it.
 */
export function useLiveEvents(deviceId: string, toUs: number, enabled: boolean, floorTsUs: number): LiveEventRow[] {
  const [rows, setRows] = useState<LiveEventRow[]>([]);
  const byEidRef = useRef<Map<string, LiveEventRow>>(new Map());
  const lastTsRef = useRef<number>(floorTsUs);
  const floorRef = useRef<number>(floorTsUs);
  floorRef.current = floorTsUs;

  // New window (day/tz change) or device → the base read takes over; drop the tail.
  useEffect(() => {
    byEidRef.current = new Map();
    lastTsRef.current = floorRef.current;
    setRows([]);
  }, [deviceId, toUs]);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const ac = new AbortController();
    const tick = async () => {
      const since = Math.max(lastTsRef.current, floorRef.current) + 1;
      if (since >= toUs) return;
      try {
        // Poll the SDCARD (not cloud): sdcard range-events carry the authoritative D16
        // record_mode/location, whereas the cloud aggregate omits record_mode — so a
        // cloud-sourced live tail showed newest events with no mode label on the timeline.
        const res = await queryRangeEvents(deviceId, since, toUs);
        const evs = res.items;
        if (!alive || evs.length === 0) return;
        let changed = false;
        for (const e of evs) {
          if (!byEidRef.current.has(e.eid)) {
            byEidRef.current.set(e.eid, { eid: e.eid, tsUs: e.event_ts_us, eventIdHex: e.event_id_hex, recordMode: e.record_mode, recordLocation: e.record_location });
            changed = true;
          }
          lastTsRef.current = Math.max(lastTsRef.current, e.event_ts_us);
        }
        if (changed) setRows([...byEidRef.current.values()]);
      } catch {
        /* ignore poll errors — the next tick retries */
      }
    };
    const id = setInterval(tick, EVENT_POLL_MS);
    return () => {
      alive = false;
      ac.abort();
      clearInterval(id);
    };
  }, [enabled, deviceId, toUs]);

  return rows;
}
